import { ConfigDatabase } from '../../engine/database';
import { sectorsData, type SectorConfig } from '../../generated/sectors';
import { galaxiesData, type GalaxyConfig } from '../../generated/galaxies';
import { map } from 'nanostores';

export type GalaxyId = string;
export type SectorId = number; // sector idx

/**
 * Интерфейс для текущего прогресса по секторам
 */
export interface SectorProgress {
  currentGalaxy: GalaxyId;
  currentSector: SectorId;
  unlockedSectors: SectorId[];
  completedSectors: SectorId[];
}

/**
 * Интерфейс для информации о секторе с дополнительными данными
 */
export interface SectorInfo extends SectorConfig {
  galaxyInfo: GalaxyConfig;
  isUnlocked: boolean;
  isCompleted: boolean;
  isCurrent: boolean;
  difficulty: number;
}

/**
 * База данных для секторов и галактик
 */
export class SectorsDB {
  private sectorsDB: ConfigDatabase<SectorConfig, number>;
  private galaxiesDB: ConfigDatabase<GalaxyConfig, string>;

  // Reactive state for current progress
  private progress = map<SectorProgress>({
    currentGalaxy: 'G1',
    currentSector: 1,
    unlockedSectors: [1], // First sector unlocked by default
    completedSectors: [],
  });

  constructor() {
    // Create sectors database indexed by sector idx
    const sectorsRecord: Record<number, SectorConfig> = {};
    sectorsData.forEach(sector => {
      sectorsRecord[sector.idx] = sector;
    });

    this.sectorsDB = new ConfigDatabase({
      base: sectorsRecord,
      overrides: {},
      getId: config => config.idx,
      validate: (config, id) => {
        if (config.hp_mult <= 0) {
          throw new Error(`Sector ${id}: hp_mult must be > 0`);
        }
        if (config.density <= 0) {
          throw new Error(`Sector ${id}: density must be > 0`);
        }
      },
    });

    this.galaxiesDB = new ConfigDatabase({
      base: galaxiesData,
      overrides: {},
      getId: config => config.galaxy_id,
      validate: (config, id) => {
        if (config.base_hp_mult <= 0) {
          throw new Error(`Galaxy ${id}: base_hp_mult must be > 0`);
        }
      },
    });
  }

  /**
   * Инициализация всех внутренних баз данных
   */
  init(): void {
    this.sectorsDB.init();
    this.galaxiesDB.init();
  }

  // ===== PROGRESS MANAGEMENT =====

  /**
   * Получить текущий прогресс
   */
  getProgress(): SectorProgress {
    return this.progress.get();
  }

  /**
   * Подписаться на изменения прогресса
   */
  subscribeProgress(callback: (progress: SectorProgress) => void) {
    return this.progress.subscribe(callback);
  }

  /**
   * Установить текущий сектор
   */
  setCurrentSector(sectorId: SectorId): boolean {
    const progress = this.progress.get();
    if (!progress.unlockedSectors.includes(sectorId)) {
      return false; // Sector not unlocked
    }

    const sector = this.getSectorInfo(sectorId);
    if (!sector) return false;

    this.progress.set({
      ...progress,
      currentSector: sectorId,
      currentGalaxy: sector.galaxyInfo.galaxy_id,
    });

    return true;
  }

  /**
   * Разблокировать сектор
   */
  unlockSector(sectorId: SectorId): boolean {
    const progress = this.progress.get();
    if (progress.unlockedSectors.includes(sectorId)) {
      return false; // Already unlocked
    }

    this.progress.set({
      ...progress,
      unlockedSectors: [...progress.unlockedSectors, sectorId].sort(),
    });

    return true;
  }

  /**
   * Отметить сектор как завершенный
   */
  completeSector(sectorId: SectorId): boolean {
    const progress = this.progress.get();
    if (progress.completedSectors.includes(sectorId)) {
      return false; // Already completed
    }

    this.progress.set({
      ...progress,
      completedSectors: [...progress.completedSectors, sectorId].sort(),
    });

    // Auto-unlock next sector
    this.unlockSector(sectorId + 1);

    return true;
  }

  // ===== SECTOR INFO =====

  /**
   * Получить информацию о секторе
   */
  getSectorInfo(sectorId: SectorId): SectorInfo | undefined {
    const sector = this.sectorsDB.get(sectorId);
    if (!sector) return undefined;

    const galaxyInfo = this.galaxiesDB.get(sector.Galaxy);
    if (!galaxyInfo) return undefined;

    const progress = this.progress.get();
    const isUnlocked = progress.unlockedSectors.includes(sectorId);
    const isCompleted = progress.completedSectors.includes(sectorId);
    const isCurrent = progress.currentSector === sectorId;

    // Calculate difficulty based on HP multiplier and sector index
    const difficulty = sector.hp_mult * galaxyInfo.base_hp_mult * sector.idx;

    return {
      ...sector,
      galaxyInfo,
      isUnlocked,
      isCompleted,
      isCurrent,
      difficulty,
    };
  }

  /**
   * Получить текущий активный сектор
   */
  getCurrentSector(): SectorInfo | undefined {
    const progress = this.progress.get();
    return this.getSectorInfo(progress.currentSector);
  }

  /**
   * Получить все секторы
   */
  getAllSectors(): SectorInfo[] {
    return this.sectorsDB
      .all()
      .map(sector => this.getSectorInfo(sector.idx)!)
      .filter(sector => sector !== undefined);
  }

  /**
   * Получить секторы по галактике
   */
  getSectorsByGalaxy(galaxyId: GalaxyId): SectorInfo[] {
    return this.getAllSectors().filter(sector => sector.Galaxy === galaxyId);
  }

  /**
   * Получить разблокированные секторы
   */
  getUnlockedSectors(): SectorInfo[] {
    return this.getAllSectors().filter(sector => sector.isUnlocked);
  }

  /**
   * Получить завершенные секторы
   */
  getCompletedSectors(): SectorInfo[] {
    return this.getAllSectors().filter(sector => sector.isCompleted);
  }

  // ===== GALAXY INFO =====

  /**
   * Получить информацию о галактике
   */
  getGalaxyInfo(galaxyId: GalaxyId): GalaxyConfig | undefined {
    return this.galaxiesDB.get(galaxyId);
  }

  /**
   * Получить все галактики
   */
  getAllGalaxies(): GalaxyConfig[] {
    return this.galaxiesDB.all();
  }

  // ===== UTILITY METHODS =====

  /**
   * Получить микс астероидов для текущего сектора
   */
  getCurrentAsteroidMix(): string {
    const currentSector = this.getCurrentSector();
    return currentSector?.asteroid_mix_id || 'MIX_AURORA';
  }

  /**
   * Получить пул лута для текущего сектора
   */
  getCurrentLootPool(): string {
    const currentSector = this.getCurrentSector();
    return currentSector?.base_loot_pool_id || 'LP_COMMON';
  }

  /**
   * Получить модификаторы сложности для текущего сектора
   */
  getCurrentDifficultyModifiers(): {
    hpMult: number;
    armorAdd: number;
    resistLaser: number;
    resistBomb: number;
    density: number;
  } {
    const currentSector = this.getCurrentSector();
    if (!currentSector) {
      return {
        hpMult: 1,
        armorAdd: 0,
        resistLaser: 0,
        resistBomb: 0,
        density: 1,
      };
    }

    return {
      hpMult: currentSector.hp_mult * currentSector.galaxyInfo.base_hp_mult,
      armorAdd: currentSector.armor_add + currentSector.galaxyInfo.base_armor_step,
      resistLaser:
        currentSector.res_laser + (currentSector.galaxyInfo.base_resist_profile as any)?.laser || 0,
      resistBomb:
        currentSector.res_bomb + (currentSector.galaxyInfo.base_resist_profile as any)?.bomb || 0,
      density: currentSector.density,
    };
  }

  /**
   * Получить статистику прогресса
   */
  getProgressStats(): {
    totalSectors: number;
    unlockedSectors: number;
    completedSectors: number;
    currentSector: number;
    currentGalaxy: string;
    progressPercentage: number;
  } {
    const allSectors = this.getAllSectors();
    const progress = this.progress.get();

    return {
      totalSectors: allSectors.length,
      unlockedSectors: progress.unlockedSectors.length,
      completedSectors: progress.completedSectors.length,
      currentSector: progress.currentSector,
      currentGalaxy: progress.currentGalaxy,
      progressPercentage: (progress.completedSectors.length / allSectors.length) * 100,
    };
  }

  /**
   * Сбросить прогресс (для тестирования)
   */
  resetProgress(): void {
    this.progress.set({
      currentGalaxy: 'G1',
      currentSector: 1,
      unlockedSectors: [1],
      completedSectors: [],
    });
  }
}

// Ленивый синглтон для удобства
let _sectorsInstance: SectorsDB | null = null;
export function getSectors(): SectorsDB {
  if (!_sectorsInstance) {
    _sectorsInstance = new SectorsDB();
  }
  return _sectorsInstance;
}

// Синглтон для удобства
export const Sectors = new Proxy({} as SectorsDB, {
  get(_target, prop) {
    return getSectors()[prop as keyof SectorsDB];
  },
});

// Реэкспортируем типы для удобства
export type { SectorConfig, GalaxyConfig };
